import { useState } from 'react';
import {
  SignInPage, SignInCard, SignInWordmark, SignInTagline,
  PrimaryButton, AlertBanner,
} from '../styled-components';
import { WaveformBackground } from '../components';
import { useFetchData } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME, getSafeBillingNextParam } from '../../common/utils';
import { CheckCircle } from 'lucide-react';

interface TierConfig {
  id: string;
  name: string;
  monthly_fee_dollars: number;
  included_minutes: number;
  overage_rate_dollars: number;
}

interface BillingTiers {
  tiers: TierConfig[];
}

// ---------------------------------------------------------------------------
// Tier card
// ---------------------------------------------------------------------------

function TierCard({
  tier,
  selected,
  onSelect,
}: {
  tier: TierConfig;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      style={{
        background: selected ? 'rgba(0,196,232,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${selected ? 'rgba(0,196,232,0.5)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 10,
        padding: '16px 18px',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'border-color 150ms ease, background 150ms ease',
        position: 'relative',
      }}
    >
      {selected && (
        <CheckCircle
          size={15}
          style={{ position: 'absolute', top: 12, right: 12, color: 'var(--accent-cyan)' }}
        />
      )}
      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
        {tier.name}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: selected ? 'var(--accent-cyan)' : '#fff', marginBottom: 8 }}>
        ${tier.monthly_fee_dollars.toFixed(0)}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)' }}>/mo</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {tier.included_minutes.toLocaleString()} min/yr included<br />
        ${tier.overage_rate_dollars.toFixed(2)}/min overage
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Billing setup page
// ---------------------------------------------------------------------------

export function BillingSetupPage() {
  const { data: tiersData, loading } = useFetchData<BillingTiers>('/dashboard/api/billing/tiers', { tiers: [] });
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Post-setup destination (e.g. a CLI-login /oauth/authorize URL).
  const nextParam = getSafeBillingNextParam();

  async function handleContinue() {
    if (!selectedTier) return;
    setSubmitting(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tier: selectedTier });
      if (nextParam) params.set('next', nextParam);
      const res = await fetchOrThrow(`/dashboard/api/billing/choose-package-tier?${params}`, {
        method: 'POST',
        headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <SignInPage>
      <WaveformBackground />
      <SignInWordmark>
        <img src="/static/guava-logo.png" alt="Guava" style={{ height: 250, filter: 'grayscale(1) brightness(0.75)', margin: '-80px 0' }} />
      </SignInWordmark>
      <SignInTagline>Choose a plan to get started</SignInTagline>

      <div style={{ width: 420, maxWidth: 'calc(100vw - 40px)', position: 'relative', zIndex: 1 }}>
        <SignInCard style={{ gap: 10 }}>
          {loading ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: '12px 0' }}>
              Loading plans…
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tiersData.tiers.map((tier) => (
                  <TierCard
                    key={tier.id}
                    tier={tier}
                    selected={selectedTier === tier.id}
                    onSelect={() => setSelectedTier(tier.id)}
                  />
                ))}
              </div>

              {error && (
                <AlertBanner variant="red" style={{ marginBottom: 0 }}>{error}</AlertBanner>
              )}

              <PrimaryButton
                onClick={handleContinue}
                disabled={!selectedTier || submitting}
                style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
              >
                { submitting ? 'Redirecting…'
                  : selectedTier === 'free' ? 'Continue'
                  : 'Continue to Payment'
                }
              </PrimaryButton>

              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                A payment method is required for non-Free plans. Overages are billed monthly as you exceed your annual limit.
              </div>
            </>
          )}
        </SignInCard>
      </div>
    </SignInPage>
  );
}
